import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { FileText, ThumbsUp, ThumbsDown } from "lucide-react-native";
import {
  IconInput,
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useInfertility } from "../InfertilityContext";
import type { InfertilityStackParamList } from "../InfertilityNavigator";

export function PartnerInfertilityDiagnosisScreen() {
  const t = useTranslations();
  const { state, updateState } = useInfertility();
  const navigation = useNavigation<NavigationProp<InfertilityStackParamList>>();

  const selection: "yes" | "no" | null =
    state.partnerHasDiagnosis === null
      ? null
      : state.partnerHasDiagnosis
        ? "yes"
        : "no";

  // Older records stored cause and comments separately. Present both in the
  // new single field and canonicalize the value when the patient edits it.
  const partnerDetails = [state.partnerCause, state.partnerComments]
    .filter((value): value is string => !!value?.trim())
    .join("\n\n");

  const handleSave = () => {
    navigation.navigate("InfertilityComplete");
  };

  return (
    <QuestionLayout
      title={t("med_history.fertility.infertility.partner_diagnosis_title")}
      subtitle={t(
        "med_history.fertility.infertility.partner_diagnosis_subtitle",
      )}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={
        !selection || (selection === "yes" && !partnerDetails.trim())
      }
      saveLabel={t("med_history.fertility.continue")}
    >
      <View style={styles.optionsContainer}>
        <SquareOptionButton
          icon={ThumbsUp}
          label={t("med_history.shared.yes")}
          isSelected={selection === "yes"}
          onPress={() => updateState({ partnerHasDiagnosis: true })}
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
        <SquareOptionButton
          icon={ThumbsDown}
          label={t("med_history.shared.no")}
          isSelected={selection === "no"}
          onPress={() =>
            updateState({
              partnerHasDiagnosis: false,
              partnerCause: null,
              partnerComments: null,
            })
          }
          iconSize={24}
          containerSize={48}
          labelSize="lg"
        />
      </View>
      {selection === "yes" && (
        <View style={styles.detailsContainer}>
          <IconInput
            label={t("med_history.fertility.infertility.partner_details_label")}
            icon={FileText}
            placeholder={t(
              "med_history.fertility.infertility.partner_details_placeholder",
            )}
            value={partnerDetails}
            onChangeText={(text) =>
              updateState({ partnerCause: text, partnerComments: null })
            }
            multiline
            numberOfLines={6}
            autoCapitalize="sentences"
          />
        </View>
      )}
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 24,
    width: "100%",
  },
  detailsContainer: {
    marginBottom: 40,
    width: "100%",
  },
});

export default PartnerInfertilityDiagnosisScreen;
